const React = require('react');
const T = require('prop-types');

const internals = {};

internals.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
internals.arcLengthRatio = 0.25;
internals.spinDurationMs = 900;
internals.screenLoaderStyles = {
    loaderSize: 100,
    gap: 24,
    radius: 38,
    strokeWidth: 7,
    textFontSize: 20,
    textLineHeight: '24px',
    tileRadius: 26
};
internals.contentLoaderStyles = {
    loaderSize: 72,
    gap: 14,
    radius: 34,
    strokeWidth: 5,
    textFontSize: 14,
    textLineHeight: '18px',
    tileRadius: 22
};

module.exports = function BootLoader(props) {

    const {
        className,
        message = 'Loading...',
        variant = 'screen'
    } = props;

    const isContent = variant === 'content';
    const styles = internals.getStyles(isContent);
    const classes = [
        'nearpeer-loading-screen',
        `nearpeer-loading-screen--${variant}`,
        className
    ].filter(Boolean).join(' ');

    React.useLayoutEffect(() => {

        if (!isContent && typeof window !== 'undefined' && window.__nearpeerSetBootLoaderMessage) {
            window.__nearpeerSetBootLoaderMessage(message);
        }
    }, [isContent, message]);

    React.useEffect(() => {

        if (isContent || typeof window === 'undefined' || !window.__nearpeerDismissBootLoader) {
            return undefined;
        }

        const frameId = window.requestAnimationFrame
            ? window.requestAnimationFrame(window.__nearpeerDismissBootLoader)
            : null;

        if (frameId === null) {
            window.__nearpeerDismissBootLoader();
            return undefined;
        }

        return () => {

            if (window.cancelAnimationFrame) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [isContent]);

    return (
        <div
            className={classes}
            role='status'
            aria-live='polite'
            aria-label={message}
            style={styles.root}
        >
            <div
                className='nearpeer-loading-screen__stack'
                style={styles.stack}
            >
                <internals.Loader styles={styles} />
                <div
                    className='nearpeer-loading-screen__text'
                    style={styles.text}
                >
                    {message}
                </div>
            </div>
        </div>
    );
};

module.exports.propTypes = {
    className: T.string,
    message: T.string,
    variant: T.oneOf(['screen', 'content'])
};

internals.Loader = function Loader({ styles }) {

    const arcRef = React.useRef(null);
    const animationStartedAt = React.useRef(null);
    const {
        activeColor,
        center,
        circumference,
        radius,
        size,
        strokeWidth,
        trackColor
    } = styles.loaderMetrics;

    if (animationStartedAt.current === null) {
        animationStartedAt.current = internals.getAnimationStartedAt(styles.isContent);
    }

    const initialAngle = internals.getAnimationAngle(animationStartedAt.current);

    React.useLayoutEffect(() => {

        if (typeof window === 'undefined' || !arcRef.current || !window.requestAnimationFrame) {
            return undefined;
        }

        let frameId;
        const tick = () => {

            const angle = internals.getAnimationAngle(animationStartedAt.current);

            if (arcRef.current) {
                arcRef.current.setAttribute('transform', `rotate(${angle} ${center} ${center})`);
            }

            frameId = window.requestAnimationFrame(tick);
        };

        tick();

        return () => {

            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [center]);

    return (
        <svg
            className='nearpeer-loading-screen__loader'
            xmlns='http://www.w3.org/2000/svg'
            viewBox={`0 0 ${size} ${size}`}
            style={styles.loader}
            aria-hidden='true'
        >
            <rect
                x='0'
                y='0'
                width={size}
                height={size}
                rx={styles.tileRadius}
                fill={styles.loaderBackground}
            />

            <circle
                cx={center}
                cy={center}
                r={radius}
                fill='none'
                stroke={trackColor}
                strokeWidth={strokeWidth}
            />

            <circle
                ref={arcRef}
                cx={center}
                cy={center}
                r={radius}
                fill='none'
                stroke={activeColor}
                strokeLinecap='round'
                strokeWidth={strokeWidth}
                strokeDasharray={`${circumference * internals.arcLengthRatio} ${circumference}`}
                transform={`rotate(${initialAngle} ${center} ${center})`}
            />

            <g transform='translate(30 30) scale(0.72)'>
                <path
                    fill='#ff4081'
                    stroke='#ff4081'
                    strokeWidth='1.8'
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    d='M13.89978,36.39453V9.8999h-8V36.39471a9.9,9.9,0,1,0,8-.00018Z'
                />
                <path
                    fill='#ff4081'
                    stroke='#ff4081'
                    strokeWidth='1.8'
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    d='M55.2,9.9a9.9,9.9,0,1,0-13.89978,9.05151V46.2998h8V18.95135A9.89683,9.89683,0,0,0,55.2,9.9Z'
                />
                <path
                    fill='#4e00b0'
                    stroke='#4e00b0'
                    strokeWidth='1.8'
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    d='M45.3,35.546a9.85724,9.85724,0,0,0-3.55.66693L19.119,13.48834a9.92919,9.92919,0,1,0-5.66937,5.6449l22.63129,22.7243A9.89541,9.89541,0,1,0,45.3,35.546Z'
                />
            </g>
        </svg>
    );
};

internals.Loader.propTypes = {
    styles: T.object.isRequired
};

internals.now = () => {

    if (typeof window !== 'undefined' && window.performance?.now) {
        return window.performance.now();
    }

    return Date.now();
};

internals.getAnimationStartedAt = (isContent) => {

    if (typeof window === 'undefined') {
        return 0;
    }

    const startedAtKey = isContent
        ? '__nearpeerContentLoadingScreenStartedAt'
        : '__nearpeerLoadingScreenStartedAt';
    const startedAt = window[startedAtKey];

    if (typeof startedAt === 'number') {
        return startedAt;
    }

    const nextStartedAt = internals.now();

    window[startedAtKey] = nextStartedAt;

    return nextStartedAt;
};

internals.getAnimationAngle = (startedAt) => {

    const elapsed = Math.max(internals.now() - startedAt, 0);
    const phase = (elapsed % internals.spinDurationMs) / internals.spinDurationMs;

    return (phase * 360) - 90;
};

internals.getStyles = (isContent) => {

    const metrics = isContent ? internals.contentLoaderStyles : internals.screenLoaderStyles;
    const size = 100;
    const center = 50;
    const circumference = 2 * Math.PI * metrics.radius;

    return {
        isContent,
        root: {
            minHeight: isContent ? 180 : '100vh',
            width: isContent ? '100%' : '100vw',
            height: isContent ? '100%' : '100vh',
            position: isContent ? 'relative' : 'fixed',
            top: isContent ? undefined : 0,
            right: isContent ? undefined : 0,
            bottom: isContent ? undefined : 0,
            left: isContent ? undefined : 0,
            zIndex: isContent ? undefined : 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isContent ? 'transparent' : '#4611a9',
            color: isContent ? '#4611a9' : '#ffffff',
            fontFamily: internals.fontFamily,
            fontSynthesis: 'none',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            overflow: 'hidden'
        },
        stack: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: metrics.gap
        },
        loader: {
            width: metrics.loaderSize,
            height: metrics.loaderSize,
            display: 'block',
            overflow: 'visible',
            flex: '0 0 auto'
        },
        tileRadius: metrics.tileRadius,
        loaderBackground: isContent ? 'rgba(70, 17, 169, 0.10)' : 'rgba(255, 255, 255, 0.30)',
        loaderMetrics: {
            activeColor: '#a92a54',
            center,
            circumference,
            radius: metrics.radius,
            size,
            strokeWidth: metrics.strokeWidth,
            trackColor: isContent ? 'rgba(70, 17, 169, 0.14)' : 'rgba(255, 255, 255, 0.25)'
        },
        text: {
            color: isContent ? '#4611a9' : '#ffffff',
            textAlign: 'center',
            width: '100%',
            padding: '0 16px',
            boxSizing: 'border-box',
            fontFamily: internals.fontFamily,
            fontSize: metrics.textFontSize,
            lineHeight: metrics.textLineHeight,
            fontWeight: 700,
            fontSynthesis: 'none',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
        }
    };
};
